import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ButtonWithIcon } from '@els/els-react--button';
import { LinkElement } from '@els/els-react--link-element';
import { ModalPanel } from '@els/els-react--modal-panel';
import history from '@AppRoutes/history';
import NamedUserContainer from '@Containers/Header/DashboardNavigation/NamedUser/NamedUserContainer';
import HoverTooltip from '@Components/HoverTooltip';
import GeneralMenuItems from '../GeneralMenuItems';

import './HamburgerMenu.scss';

export default function HamburgerMenu({ isAnonymousUser, openRegisterDialog, goToLogin }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [pathname, setPathname] = useState(history.location.pathname);
  useEffect(() => {
    const unlisten = history.listen((location) => setPathname(location.pathname));
    return unlisten;
  }, []);
  const isLoginPage = pathname === '/login';
  const isRegisterPage = pathname === '/register' || pathname === '/security/register';
  const options = [
    {
      id: 'access-dropdown-register',
      text: 'Register',
      iconSprite: 'AddUser',
      ariaLabel: 'Open registration dialog',
      href: '#',
      disabled: isRegisterPage,
      onClick: (e) => {
        e.preventDefault();
        Logger.info('Register');
        setIsMenuOpen(false);
        openRegisterDialog();
      },
      a11y: { name: 'Register', description: 'Open registration dialog' },
    },
    {
      id: 'access-dropdown-login',
      text: 'Sign in',
      iconSprite: 'LogIn',
      ariaLabel: 'Go to login page',
      href: '#',
      disabled: isLoginPage,
      onClick: (e) => {
        e.preventDefault();
        Logger.info('Log in');
        goToLogin();
      },
      a11y: { name: 'Login', description: 'Go to login page' },
    },
  ];

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  return (
    <li className="hamburger-menu-toggler u-els-display-none u-els-display-inline@tablet">
      <HoverTooltip id="main-menu-tooltip" tooltip="Main Menu" tooltipPlace="bottom-end" theme="simple">
        <ButtonWithIcon
          data-e2e="hamburger-menu-toggle"
          id="hamburger-menu-toggler"
          sprite="Menu"
          type="borderless"
          iconOnly
          aria-expanded={isMenuOpen}
          aria-controls="main-menu-panel"
          className="u-els-margin-left-1x rx-cursor-pointer"
          onClick={() => setIsMenuOpen((open) => !open)}>
          Menu Option
        </ButtonWithIcon>
      </HoverTooltip>
      {isMenuOpen && (
        <ModalPanel
          id="main-menu-panel"
          label="Main Menu"
          className="hamburger-menu-modal-panel"
          hideBackground={false}
          panelPosition="right"
          panelSize="small"
          modalProps={{
            label: 'Main Menu',
            onClose: closeMenu,
            shouldHideCloseButton: true,
          }}>
          <div className="hamburger-menu-panel">
            <div className="hamburger-menu-panel__header">
              <h2 className="u-els-font-size-h2">Main Menu</h2>
              <ButtonWithIcon
                aria-label="Close Main Menu"
                className="hamburger-menu-panel__close"
                data-e2e="hamburger-menu-close"
                iconOnly
                id="hamburger-menu-close"
                onClick={closeMenu}
                sprite="Close"
                type="borderless">
                Close Main Menu
              </ButtonWithIcon>
            </div>
            <GeneralMenuItems onItemClick={closeMenu} />
            {isAnonymousUser && (
              <>
                <div className="u-els-margin-bottom-3o4" aria-label="Reaxys Access options">
                  <h4 className="u-els-font-size-h4">Reaxys Access</h4>
                </div>
                <ul className="u-els-font-size-body">
                  {options.map((option) => (
                    <li key={option.id} className="u-els-font-size-body u-els-margin-bottom-1o2">
                      <LinkElement
                        data-e2e="hamburger-menu-item"
                        iconSprite={option.iconSprite}
                        href={option.href}
                        disabled={option.disabled}
                        onClick={option.onClick}
                        a11y={option.a11y}
                        className={option.className}>
                        {option.text}
                      </LinkElement>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {!isAnonymousUser && (
              <>
                <hr className="c-els-divider u-els-margin-top-1o4 u-els-margin-bottom-1o4" />
                <NamedUserContainer closeFlyout={closeMenu} iconSize="s" isSidePanel />
              </>
            )}
          </div>
        </ModalPanel>
      )}
    </li>
  );
}

HamburgerMenu.propTypes = {
  openRegisterDialog: PropTypes.func.isRequired,
  goToLogin: PropTypes.func.isRequired,
  isAnonymousUser: PropTypes.bool,
};
